import React, { useState, useRef, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { User, LogOut, ChevronDown, Crown, Settings } from 'lucide-react'
import { useAuthStore } from '../stores/authStore'
import { useDecisionAnalysisStore } from '../stores/decisionAnalysisStore'

export const UserDropdown: React.FC = () => {

  const { user, signOut, loading } = useAuthStore()
  const { decisionHistory } = useDecisionAnalysisStore()
  const [showDropdown, setShowDropdown] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleSignOut = async () => {
    await signOut()
    setShowDropdown(false)
  }

  const totalHistoryCount = decisionHistory.length

  if (loading) {
    return (
      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600"></div>
    )
  }

  if (!user) {
    return (
      <Link
        to="/auth"
        className="flex items-center space-x-1 px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md transition-colors"
      >
        <User className="w-4 h-4" />
        <span>Sign In</span>
      </Link>
    )
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 transition-colors"
      >
        <div className="relative">
          <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
            <User className="w-4 h-4 text-white" />
          </div>
          {/* Premium indicator as superscript */}
          <div className="absolute -top-1 -right-1 w-4 h-4 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-full flex items-center justify-center shadow-sm">
            <Crown className="w-2.5 h-2.5 text-white" />
          </div>
        </div>
        <span className="text-sm font-medium">
          {user.user_metadata?.full_name || user.email?.split('@')[0] || 'User'}
        </span>
        <ChevronDown className={`w-4 h-4 transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
      </button>

      {showDropdown && (
        <div className="absolute top-full right-0 mt-1 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
          {/* User Info Section */}
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="relative">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                  <User className="w-6 h-6 text-white" />
                </div>
                <div className="absolute -top-1 -right-1 w-5 h-5 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-full flex items-center justify-center shadow-sm">
                  <Crown className="w-3 h-3 text-white" />
                </div>
              </div>
              <div className="flex-1">
                <div className="font-medium text-gray-900">
                  {user.user_metadata?.full_name || 'User'}
                </div>
                <div className="text-sm text-gray-500">
                  {user.email}
                </div>
                <div className="text-xs text-yellow-600 font-medium mt-1">
                  Premium User
                </div>
              </div>
            </div>
          </div>

          {/* History Section */}
          <div className="p-2">
            <div className="px-2 py-1 text-xs font-medium text-gray-500 uppercase tracking-wide">
              Analysis History
            </div>
            <Link
              to="/history"
              onClick={() => setShowDropdown(false)}
              className="flex items-center space-x-3 p-3 rounded-md text-gray-700 hover:bg-gray-50 transition-colors w-full"
            >
              <span className="text-lg">📊</span>
              <div className="flex-1">
                <div className="font-medium text-sm">View All History</div>
                <div className="text-xs text-gray-500">View all your analysis history</div>
              </div>
              {totalHistoryCount > 0 && (
                <div className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">
                  {totalHistoryCount}
                </div>
              )}
            </Link>
          </div>

          {/* Settings Section */}
          <div className="p-2 border-t border-gray-200">
            <Link
              to="/settings"
              onClick={() => setShowDropdown(false)}
              className="flex items-center space-x-3 p-3 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <Settings className="w-4 h-4" />
              <span className="text-sm font-medium">Settings</span>
            </Link>
          </div>

          {/* Sign Out Section */}
          <div className="p-2 border-t border-gray-200">
            <button
              onClick={handleSignOut}
              className="flex items-center space-x-3 p-3 rounded-md text-red-600 hover:bg-red-50 transition-colors w-full"
            >
              <LogOut className="w-4 h-4" />
              <span className="text-sm font-medium">Sign Out</span>
            </button>
          </div>
        </div>
      )}
    </div>
  )
} 